import { Injectable } from '@angular/core';
import { DataRepository, FlatTableDataRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { BusinessLayerService, DataByTableTypeProvider } from '@domain/business-layer';
import { DataModel, PrimaryKeyValueModel, ReferenceTableModel, SurroundingIntervalsModel } from '@shared';
import { iif, Observable, of, combineLatest } from 'rxjs';
import { first, map, switchMap, catchError  } from 'rxjs/operators';
import { QueryParamsProvider } from '../../../routing/services/query-params.provider';
import { TableTypeProvider } from '../../../business-layer/services/table-type.provider';
import { DropdownDataSourceProvider } from '../../services/dropdown-data-source.provider';

@Injectable()
export class IntervalService {
	public constructor(
		private dataRepository: DataRepository,
		private tableTypeProvider: TableTypeProvider,
		private dropdownDataSource: DropdownDataSourceProvider,
		private businessLayerService: BusinessLayerService,
		private flatTableDataRepository: FlatTableDataRepository,
		private queryParamsProvider: QueryParamsProvider,
		private dataByTableTypeProvider: DataByTableTypeProvider
	) {}

	public isIntervalBreached$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: string,
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		return this.dataByTableTypeProvider
			.getOutsideIntervalData$(
				tableName,
				workfileId,
				projectNumber,
				entityName,
				entryId,
				childEntryId,
				depthFrom,
				depthTo
			)
			.pipe(map((tableData: DataModel[]) => (tableData.length === 0 ? false : true)));
	}

	public isOverlappingData$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: string,
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		return this.dataByTableTypeProvider
			.getOverlappingData$(
				tableName,
				workfileId,
				projectNumber,
				entityName,
				entryId,
				childEntryId,
				depthFrom,
				depthTo
			)
			.pipe(map((tableData: DataModel) => (tableData ? true : false)));
	}

	public isFirstRecord$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: string
	): Observable<boolean> {
		return this.dataByTableTypeProvider
			.hasAnyChildTableData$(tableName, workfileId, projectNumber, entityName, entryId, childEntryId)
			.pipe(
				switchMap((hasAny: boolean) =>
					iif(
						() => hasAny,
						this.dataByTableTypeProvider
							.hasAnyStartsAtZeroData$(
								tableName,
								workfileId,
								projectNumber,
								entityName,
								entryId,
								childEntryId
							)
							.pipe(map((hasAnyStartAtZero: boolean) => !hasAnyStartAtZero)),
						of(true)
					)
				)
			);
	}

	public isDuplicateInterval$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: string,
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		return this.tableTypeProvider
			.isFlatSamplesTable$(this.queryParamsProvider.workfileId, this.queryParamsProvider.parentTableName)
			.pipe(
				first(),
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						this.isDuplicateFLatSample$(entityName, projectNumber, Number(entryId), depthFrom, depthTo),
						this.businessLayerService
							.getPrimaryKeyAndValue$(workfileId, projectNumber, entityName, Number(entryId))
							.pipe(
								switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
									this.tableTypeProvider
										.isSampleTable$(tableName)
										.pipe(
											switchMap((isSampleTable: boolean) =>
												isSampleTable
													? this.isDuplicateSample$(
															tableName,
															projectNumber,
															primaryKeyValue,
															Number(childEntryId),
															depthFrom,
															depthTo,
															[]
													  )
													: this.dataRepository.getDuplicateDataByDepths$(
															tableName.toLowerCase(),
															projectNumber,
															primaryKeyValue,
															[],
															depthFrom,
															depthTo
													  )
											)
										)
								)
							)
					)
				)
			);
	}

	public isDuplicateData$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: string,
		depthFrom?: number,
		depthTo?: number
	): Observable<boolean> {
		return this.tableTypeProvider
			.isFlatSamplesTable$(this.queryParamsProvider.workfileId, this.queryParamsProvider.parentTableName)
			.pipe(
				first(),
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						this.isDuplicateFLatSample$(entityName, projectNumber, Number(entryId), depthFrom, depthTo),
						this.businessLayerService
							.getPrimaryKeyAndValue$(workfileId, projectNumber, entityName, Number(entryId))
							.pipe(
								switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
									this.tableTypeProvider
										.isSampleTable$(tableName)
										.pipe(
											switchMap((isSampleTable: boolean) =>
												isSampleTable
													? this.isDuplicateSample$(
															tableName,
															projectNumber,
															primaryKeyValue,
															Number(childEntryId),
															depthFrom,
															depthTo,
															[Number(childEntryId)]
													  )
													: this.dataRepository.getDuplicateDataByDepths$(
															tableName.toLowerCase(),
															projectNumber,
															primaryKeyValue,
															[Number(childEntryId)],
															depthFrom,
															depthTo
													  )
											)
										)
								)
							)
					)
				)
			);
	}

	/**
	 * Returns true when an interval duplicate exists but ONLY for the current record (self-duplicate).
	 * This composes existing checks to avoid adding new repository queries:
	 * - isDuplicateInterval$: duplicates including current record
	 * - isDuplicateData$: duplicates excluding current record (via excludedIds)
	 *
	 * Therefore: owned-by-current = isDuplicateInterval && !isDuplicateData
	 */
	public isDuplicateOwnedByCurrentEntry$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: string,
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		return combineLatest([
			this.isDuplicateInterval$(
				tableName,
				workfileId,
				projectNumber,
				entityName,
				entryId,
				childEntryId,
				depthFrom,
				depthTo
			),
			this.isDuplicateData$(
				tableName,
				workfileId,
				projectNumber,
				entityName,
				entryId,
				childEntryId,
				depthFrom,
				depthTo
			)
		]).pipe(
			map(([dupAny, dupExcludingSelf]) => dupAny && !dupExcludingSelf)
		);
	}

	public isDupsDirection$(
		hole_number: string,
		value: number
	): Observable<boolean> {
		return this.dataRepository.getTableData$('DRILL_HOLE_DIRECTION', 'DM_2017').pipe(
			map((listData) => {
				const consistData = listData
					.filter(item => item.hole_number === hole_number)
					.map(item => item.depth);
				return !consistData.includes(value); 
			}),
			catchError((error) => {
				console.error('Error fetching data:', error);
				return of(false); 
			})
		);
	}

	public isGapInData$(
		tableName: string,
		workfileId: string,
		projectNumber: string,
		entityName: string,
		entryId: string,
		childEntryId: string,
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		return this.dataByTableTypeProvider
			.getSurroundingIntervals$(
				tableName,
				workfileId,
				projectNumber,
				entityName,
				entryId,
				childEntryId,
				depthFrom,
				depthTo
			)
			.pipe(
				map((surroundingIntervals: SurroundingIntervalsModel) => {
					if (surroundingIntervals.previousInterval && !surroundingIntervals.nextInterval) {
						return (
							Number(surroundingIntervals.previousInterval[WorkspaceDatabaseKeys.columns.depthTo]) <
							depthFrom
						);
					}

					if (surroundingIntervals.nextInterval && !surroundingIntervals.previousInterval) {
						return (
							Number(surroundingIntervals.nextInterval[WorkspaceDatabaseKeys.columns.depthFrom]) > depthTo
						);
					}

					if (surroundingIntervals.previousInterval && surroundingIntervals.nextInterval) {
						return (
							Number(surroundingIntervals.previousInterval[WorkspaceDatabaseKeys.columns.depthTo]) <
								depthFrom ||
							Number(surroundingIntervals.nextInterval[WorkspaceDatabaseKeys.columns.depthFrom]) > depthTo
						);
					}

					return false;
				})
			);
	}

	private isDuplicateSample$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		childEntryId: number,
		depthFrom: number,
		depthTo: number,
		excludedIds: number[]
	): Observable<boolean> {
		return this.dropdownDataSource.getReferenceTablesByCategory$(true).pipe(
			map((qcRefTypes: ReferenceTableModel[]) =>
				qcRefTypes.map(
					(refTable: ReferenceTableModel) =>
						refTable[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode]
				)
			),
			switchMap((qcRefTypes: string[]) =>
				this.dataRepository
					.getOutsideIntervalData$(
						tableName.toLowerCase(),
						projectNumber,
						primaryKeyValue,
						childEntryId,
						depthFrom,
						depthTo
					)
					.pipe(
						map((intervalEntries: DataModel[]) =>
							intervalEntries.filter((intervalEntry: DataModel) =>
								qcRefTypes.includes(intervalEntry.assay_sample_type_code)
							)
						),
						map((qcIntervalEntries: DataModel[]) =>
							qcIntervalEntries.map((qcIntervalEntry: DataModel) => qcIntervalEntry.rxdbid)
						),
						switchMap((qcIntervalsIds: number[]) => {
							excludedIds = [...excludedIds, ...qcIntervalsIds];
							return this.dataRepository.getDuplicateDataByDepths$(
								tableName.toLowerCase(),
								projectNumber,
								primaryKeyValue,
								excludedIds,
								depthFrom,
								depthTo
							);
						})
					)
			)
		);
	}

	private isDuplicateFLatSample$(
		tableName: string,
		projectNumber: string,
		childEntryId: number,
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		let excludedIds: number[] = [Number(childEntryId)];

		return this.dropdownDataSource.getReferenceTablesByCategory$(true).pipe(
			map((qcRefTypes: ReferenceTableModel[]) =>
				qcRefTypes.map(
					(refTable: ReferenceTableModel) =>
						refTable[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode]
				)
			),
			switchMap((qcRefTypes: string[]) =>
				this.flatTableDataRepository
					.getOutsideIntervalData$(tableName.toLowerCase(), projectNumber, childEntryId, depthFrom, depthTo)
					.pipe(
						map((intervalEntries: DataModel[]) =>
							intervalEntries.filter((intervalEntry: DataModel) =>
								qcRefTypes.includes(intervalEntry.assay_sample_type_code)
							)
						),
						map((qcIntervalEntries: DataModel[]) =>
							qcIntervalEntries.map((qcIntervalEntry: DataModel) => qcIntervalEntry.rxdbid)
						),
						switchMap((qcIntervalsIds: number[]) => {
							excludedIds = [...excludedIds, ...qcIntervalsIds];
							return this.flatTableDataRepository.getDuplicateDataByDepths$(
								tableName.toLowerCase(),
								projectNumber,
								excludedIds,
								depthFrom,
								depthTo
							);
						})
					)
			)
		);
	}
}
